import { useRef, useState } from "react";
import { flushSync } from "react-dom";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import type { PublishedChangeReview as Review } from "../../../shared/publishedChangeReview";
import { useCommitFiles } from "../../hooks/useCommitFiles";
import { CountBadge } from "../CountBadge";
import { Text } from "../Text";
import { mergeFiles } from "../stepFilesDiff";
import { ActivityLine } from "./ActivityLine";
import { ChatDisclosure } from "./ChatDisclosure";
import { CollapsibleHeader, CollapsibleHeaderInner } from "./Expand";
import { PublishedReviewFileList } from "./PublishedReviewFileList";
import { ToolGroup } from "./ToolGroup";
import { ToolRow } from "./ToolRow";
import { ToolStatus } from "./ToolStatus";
import { Message } from "./transcriptStyles";

export function PublishedChangeReview({
  review,
  entries,
  draft = false,
  onShowDiscussion,
}: {
  review: Review;
  entries: readonly ChatEntryWithSubTables[];
  draft?: boolean;
  onShowDiscussion?: (entryId: string) => void;
}) {
  const [selected, setSelected] = useState<number | "files" | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const generating = review.status === "generating";
  const [current, currentQuery] = useCommitFiles(review.commitId, {
    enabled: !generating,
  });
  const [previous, previousQuery] = useCommitFiles(review.baseCommitId ?? "", {
    enabled: !generating && review.baseCommitId !== null,
  });
  const files =
    !generating &&
    currentQuery.type === "complete" &&
    (!review.baseCommitId || previousQuery.type === "complete")
      ? mergeFiles(
          current.map((file) => ({
            path: file.path,
            content: file.blob?.content ?? "",
          })),
          previous.map((file) => ({
            path: file.path,
            content: file.blob?.content ?? "",
          }))
        ).filter((file) => file.kind !== "unchanged")
      : [];
  const showDiscussion = (entryId: string) => {
    flushSync(() => onShowDiscussion?.(entryId));
    const transcript = containerRef.current?.closest(
      "[data-conversation-transcript]"
    );
    const target = transcript?.querySelector<HTMLElement>(
      `[data-chat-entry-id="${CSS.escape(entryId)}"]`
    );
    target?.scrollIntoView({
      block: "center",
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });
  };
  return (
    <Message
      ref={containerRef}
      role="region"
      aria-label={draft ? "Draft change review" : "Published change review"}
      style={{
        flexDirection: "column",
        gap: 12,
        marginTop:
          entries.at(-1)?.userAction?.actionType === "pushDraftToLive" ? 32 : 0,
        marginBottom: 32,
      }}
    >
      {generating ? (
        <div role="status">
          <ToolGroup
            text="Highlighting changes…"
            icon="toolbox"
            pending
            disabled
            expanded={false}
            onToggle={() => {}}
            children={null}
          />
        </div>
      ) : (
        <>
          {review.summary && (
            <>
              <ActivityLine icon="diffs">Summary of changes</ActivityLine>
              <Text variant="text-regular">{review.summary}</Text>
            </>
          )}
          {review.status === "error" && (
            <Text variant="text-small" color="var(--text-secondary)">
              3B couldn’t finish this review.
              {files.length > 0 && " You can still review the changed files."}
            </Text>
          )}
          {(review.changes.length > 0 || files.length > 0) && (
            <div>
              {review.changes.map((change, index) => {
                const changeFiles = files.filter((file) =>
                  change.paths.includes(file.path)
                );
                const discussion = entries.find(
                  (entry) => entry.id === change.discussionEntryId
                );
                return (
                  <ChatDisclosure
                    // Review groups have no IDs and their order is fixed.
                    // oxlint-disable-next-line react/no-array-index-key
                    key={index}
                    header={<ToolStatus icon="diffs" text={change.title} />}
                    expanded={selected === index}
                    onToggle={() =>
                      setSelected(selected === index ? null : index)
                    }
                    content="plain"
                  >
                    <div style={{ paddingBottom: 12 }}>
                      <ToolRow
                        variant={
                          changeFiles.length > 0 ? "through" : "terminate"
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                            padding: "8px 8px 12px",
                          }}
                        >
                          <Text variant="text-small">
                            <Text variant="text-small-md">Before</Text>{" "}
                            {change.before || "…"}
                          </Text>
                          <Text variant="text-small">
                            <Text variant="text-small-md">After</Text>{" "}
                            {change.after || "…"}
                          </Text>
                          {discussion && (
                            <CollapsibleHeader
                              onPress={() => showDiscussion(discussion.id)}
                            >
                              <CollapsibleHeaderInner>
                                <ToolStatus
                                  icon="arrow-up"
                                  text="Read the discussion in chat"
                                />
                              </CollapsibleHeaderInner>
                            </CollapsibleHeader>
                          )}
                        </div>
                      </ToolRow>
                      {changeFiles.length > 0 && (
                        <PublishedReviewFileList files={changeFiles} />
                      )}
                    </div>
                  </ChatDisclosure>
                );
              })}
              {files.length > 0 && (
                <ChatDisclosure
                  header={
                    <>
                      <ToolStatus icon="code" text="All changed files" />
                      <CountBadge
                        style={{ display: "flex", flex: "none", margin: 0 }}
                      >
                        <Text variant="text-smaller-md">{files.length}</Text>
                      </CountBadge>
                    </>
                  }
                  expanded={selected === "files"}
                  onToggle={() =>
                    setSelected(selected === "files" ? null : "files")
                  }
                  content="plain"
                >
                  <PublishedReviewFileList files={files} />
                </ChatDisclosure>
              )}
            </div>
          )}
        </>
      )}
    </Message>
  );
}
